import { isBoolean } from 'src/utils/typeguards/isBoolean';
import { isNumber } from 'src/utils/typeguards/isNumber';
import { isObject } from 'src/utils/typeguards/isObject';
import { isString } from 'src/utils/typeguards/isString';
import { FetchServiceDomain } from './domain';
import { FetchClientDomain } from './FetchClient/domain';

/**
 * Developer's note:
 *
 * This class was originally justified due to the legacy infrastructure of ZHL but with the switch to newer
 * ZGCP services, much of the logic in this class is no longer needed. Consider using the Fetch API directly or
 * refactor this class to only provide logging and automatic type coercion
 * @deprecated
 */
export class RESTServiceClient implements FetchServiceDomain.CallableServiceRESTClient {
    readonly baseURL: string;
    readonly #DEFAULT_TIMEOUT_IN_MS = 30000;

    /**
     * @see http://stackoverflow.com/questions/417142/what-is-the-maximum-length-of-a-url-in-different-browsers
     */
    readonly #MAX_URL_LENGTH = 2047;
    #fetchClient: FetchClientDomain.FetchClient;

    PATCH: <TResponseType = Record<string, unknown>, TBody = unknown>(
        givenparams: FetchServiceDomain.APIActionParams<TBody>
    ) => Promise<FetchServiceDomain.SuccessfulResponse<TResponseType> | FetchServiceDomain.NetworkErrorResponse>;
    POST: <TResponseType = Record<string, unknown>, TBody = unknown>(
        givenparams: FetchServiceDomain.APIActionParams<TBody>
    ) => Promise<FetchServiceDomain.SuccessfulResponse<TResponseType> | FetchServiceDomain.NetworkErrorResponse>;
    PUT: <TResponseType = Record<string, unknown>, TBody = unknown>(
        givenparams: FetchServiceDomain.APIActionParams<TBody>
    ) => Promise<FetchServiceDomain.SuccessfulResponse<TResponseType> | FetchServiceDomain.NetworkErrorResponse>;
    DELETE: <TResponseType = Record<string, unknown>, TBody = unknown>(
        givenparams: FetchServiceDomain.APIActionParams<TBody>
    ) => Promise<FetchServiceDomain.SuccessfulResponse<TResponseType> | FetchServiceDomain.NetworkErrorResponse>;
    GET: <TResponseType = Record<string, unknown>, TBody = unknown>(
        givenparams: FetchServiceDomain.APIActionParams<TBody>
    ) => Promise<FetchServiceDomain.SuccessfulResponse<TResponseType> | FetchServiceDomain.NetworkErrorResponse>;

    constructor(baseURL: string, fetchClient: FetchClientDomain.FetchClient) {
        this.baseURL = baseURL;
        this.#fetchClient = fetchClient;
        this.POST = async <TResponseType = Record<string, unknown>, TBody = unknown>(
            params: FetchServiceDomain.APIActionParams<TBody>
        ) => this.promise<TResponseType>({ ...params, method: FetchServiceDomain.SupportedMethod.POST });
        this.PUT = async <TResponseType = Record<string, unknown>, TBody = unknown>(
            params: FetchServiceDomain.APIActionParams<TBody>
        ) => this.promise<TResponseType>({ ...params, method: FetchServiceDomain.SupportedMethod.PUT });
        this.DELETE = async <TResponseType = Record<string, unknown>, TBody = unknown>(
            params: FetchServiceDomain.APIActionParams<TBody>
        ) => this.promise<TResponseType>({ ...params, method: FetchServiceDomain.SupportedMethod.DELETE });
        this.GET = async <TResponseType = Record<string, unknown>, TBody = unknown>(
            params: FetchServiceDomain.APIActionParams<TBody>
        ) => this.promise<TResponseType>({ ...params, method: FetchServiceDomain.SupportedMethod.GET });
        this.PATCH = async <TResponseType = Record<string, unknown>, TBody = unknown>(
            params: FetchServiceDomain.APIActionParams<TBody>
        ) => this.promise<TResponseType>({ ...params, method: FetchServiceDomain.SupportedMethod.PATCH });
    }

    async promise<TResponseType = Record<string, unknown>, TBody = unknown>(
        params: FetchServiceDomain.InternalAPIActionParams<TBody>
    ): Promise<FetchServiceDomain.SuccessfulResponse<TResponseType> | FetchServiceDomain.NetworkErrorResponse> {
        const url = this.#resolveActionEndpoint(params.method, params);
        const safeHTTPMethod = this.#ensureHTTPVerbSuitableForURLLength(params.method, url);
        const options = this.#generateFetchOptions(safeHTTPMethod, params);

        const response = await this.#fetchClient.fetch(url, options);
        try {
            /**
             * Not using .json() because some of our older APIs return
             * non-JSON responses (e.g. text/plain), but still provide
             * a JSON response body.
             */
            const text = await response.text();
            const data = !text ? {} : JSON.parse(text);
            const successfulResponse: FetchServiceDomain.SuccessfulResponse<TResponseType> = {
                type: FetchServiceDomain.ResponseType.RESOLVED,
                data,
                statusCode: response.status,
            };
            return successfulResponse;
        } catch (error) {
            throw error;
        }
    }

    isBadStatus(status: number) {
        return status < 200 || status >= 300;
    }

    generateServiceURL(
        action: string,
        options: {
            params?: FetchClientDomain.TimeoutableClientFetchConfig['body'];
            useDotNotation?: boolean;
        }
    ) {
        const { params, useDotNotation = false } = options;
        const parsedParams = this.#processServerQueryParameters(params, useDotNotation);
        return `${this.baseURL}/${action}${parsedParams.length ? `?${parsedParams}` : ''}`;
    }

    #processServerQueryParameters(
        params: FetchClientDomain.TimeoutableClientFetchConfig['body'] = {},
        useDotNotation: boolean
    ) {
        const processNestedKey = useDotNotation
            ? (keyValue: string, subKeyValue: string) => `${keyValue}.${subKeyValue}`
            : (_: string, subKeyValue: string) => subKeyValue;

        return Object.entries(params)
            .map(([key, value]) =>
                this.#recursivelyCoerceKeyValueToQueryParameter(key, value, {
                    processNestedKey,
                })
            )
            .join('&');
    }
    #recursivelyCoerceKeyValueToQueryParameter(
        key: string,
        value: unknown,
        { processNestedKey }: { processNestedKey: (key: string, subKeyValue: string) => string; encodeKey?: boolean }
    ): string {
        if (this.#isPrimitive(value)) {
            return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
        }
        if (this.#isPopulatedNonPrimitive(value)) {
            if (Array.isArray(value)) {
                return value
                    .map(item =>
                        this.#recursivelyCoerceKeyValueToQueryParameter(`${key}[]`, item, {
                            processNestedKey,
                        })
                    )
                    .join('&');
            }

            return Object.entries(value)
                .map(([subKey, subValue]) =>
                    this.#recursivelyCoerceKeyValueToQueryParameter(processNestedKey(key, subKey), subValue, {
                        processNestedKey,
                    })
                )
                .join('&');
        }
        return `${encodeURIComponent(key)}=`;
    }

    #isPopulatedNonPrimitive(value: unknown): value is FetchServiceDomain.PopulatedNonPrimitiveValue {
        const isPopulatedArray = Array.isArray(value) && !!value.length;
        const isPopulatedObject = isObject(value) && !!Object.keys(value as object).length;
        return isPopulatedArray || isPopulatedObject;
    }
    #resolveActionEndpoint(method: FetchServiceDomain.SupportedMethod, params: FetchServiceDomain.APIActionParams) {
        const joinedParams = {
            ...(isObject(params.queryParams) ? params.queryParams : {}),
            ...(isObject(params.input) ? params.input : {}),
        };
        return this.generateServiceURL(params.action, {
            useDotNotation: method !== FetchServiceDomain.SupportedMethod.GET || !!params.useDotNotation,
            /**
             * In cases where the method is POST, we don't want the input to end up in the query param
             * in case the input (which goes into the body) has values which shouldn't be exposed in the
             * URL
             */
            params: method === FetchServiceDomain.SupportedMethod.POST ? params.queryParams : joinedParams,
        });
    }
    #formatInput(contentType: string, input: Record<string, unknown> | BodyInit) {
        switch (contentType) {
            case FetchServiceDomain.SupportedContentType.TEXT:
            case FetchServiceDomain.SupportedContentType.JSON:
                /**
                 * Text Plain and JSON we leave alone. Normally text/plain we would stringify our objects,
                 * but because our services actually use text/plain as application/json (to circumvent
                 * CORS restrictions on POST requests with application/json), we leave it alone
                 */
                return input;
            case FetchServiceDomain.SupportedContentType.FORM_DATA: {
                if (input instanceof FormData) {
                    return input;
                }
                const form = new FormData();
                Object.entries(input).forEach(([field, value]) => {
                    form.append(field, value);
                });
                return form;
            }
            case FetchServiceDomain.SupportedContentType.URL_FORM_ENCODED: {
                const form = new URLSearchParams();
                Object.entries(input).forEach(([field, value]) => {
                    if (Array.isArray(value)) {
                        value.forEach(subValue => {
                            form.append(`${field}[]`, subValue);
                        });
                    } else {
                        form.append(field, value);
                    }
                });
                return form.toString();
            }
            default:
                return input;
        }
    }
    #generateFetchOptions(
        method: FetchServiceDomain.SupportedMethod,
        { timeout = this.#DEFAULT_TIMEOUT_IN_MS, headers, input = {}, accessToken }: FetchServiceDomain.APIActionParams
    ) {
        const fetchOpts: FetchClientDomain.TimeoutableClientFetchConfig = {
            method,
            timeout,
            headers: headers ?? ({} as Record<string, string>),
        };

        if (!(input instanceof FormData) && !fetchOpts.headers!['Content-Type']) {
            // "Simple" CORS requires POST Content-Type be one of:
            //   - application/x-www-form-urlencoded
            //   - multipart/form-data
            //   - text/plain
            // We use text/plain as it is the "least wrong" of the three.
            // Our API servers ignore the Content-Type header, so this
            // doesn't cause any issues. When our request body is FormData
            // we need to let the browser set this header with the
            // appropriate boundary directive
            fetchOpts.headers!['Content-Type'] = FetchServiceDomain.SupportedContentType.TEXT;
        }
        if (method !== FetchServiceDomain.SupportedMethod.GET) {
            fetchOpts.body = this.#formatInput(fetchOpts.headers!['Content-Type'], input);
        }
        if (accessToken) {
            fetchOpts.headers = {
                ...fetchOpts.headers,
                Authorization: `Bearer ${accessToken}`,
            };
        }
        return fetchOpts;
    }

    /**
     * If the GET URL is really long, we need switch to POST to avoid issues with browser or server URL length limits.
     */
    #ensureHTTPVerbSuitableForURLLength(method: FetchServiceDomain.SupportedMethod, url: string) {
        if (method !== FetchServiceDomain.SupportedMethod.GET) {
            return method;
        }
        return url.length > this.#MAX_URL_LENGTH ? FetchServiceDomain.SupportedMethod.POST : method;
    }

    #isPrimitive(value: unknown): value is FetchServiceDomain.PrimitiveValue {
        return isNumber(value) || isString(value) || isBoolean(value);
    }
}
